"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.SGWTConnectCore = exports.SGWTConnectCheckSesssionFrameAttribute = exports.SGWTConnectRenewalFrameAttribute = void 0;
var events_1 = require("events");
var isSameOrigin_1 = require("./isSameOrigin");
var isScopeListed_1 = require("./isScopeListed");
var SGWTConnectAuthorizationUri_1 = require("./SGWTConnectAuthorizationUri");
var SGWTConnectError_1 = require("./SGWTConnectError");
var SGWTConnectErrorList_1 = require("./SGWTConnectErrorList");
var SGWTConnectEventList_1 = require("./SGWTConnectEventList");
var SGWTConnectOpenIDUserInfo_1 = require("./SGWTConnectOpenIDUserInfo");
var SGWTConnectServerResponse_1 = require("./SGWTConnectServerResponse");
exports.SGWTConnectRenewalFrameAttribute = 'data-sgwt-connect-renewal-frame';
exports.SGWTConnectCheckSesssionFrameAttribute = 'data-sgwt-connect-check-session-frame';
/**
 * Main API of SGWTConnect.
 */
var SGWTConnectCore = /** @class */ (function (_super) {
    __extends(SGWTConnectCore, _super);
    function SGWTConnectCore(config, storage, fetchOpenIDMetadata) {
        var _this = _super.call(this) || this;
        _this.config = config;
        _this.storage = storage;
        _this.tokenExpirationTimer = null;
        _this.periodicalRenewalTimer = null;
        _this.periodicalRenewalBackoff = 1000;
        _this.periodicalRenewalTimeout = 30000;
        _this.openIDCheckSessionTimerInterval = 3000;
        if (_this.isAuthorized()) {
            _this.restartTokenExpirationTimer();
        }
        if (fetchOpenIDMetadata && _this.hasRequestedScope('openid')) {
            fetchOpenIDMetadata(function (maybeOpenIDMetadata) {
                if (maybeOpenIDMetadata instanceof Error) {
                    _this.emit(SGWTConnectEventList_1.EVENT_OPENID_METADATA_FETCH_ERROR, maybeOpenIDMetadata);
                }
                else {
                    _this.openIDMetadata = maybeOpenIDMetadata;
                    _this.enableOpenIDSessionManagement();
                }
            });
        }
        return _this;
    }
    //
    // Public
    // --------------------
    /**
     * Initiates the authorization process.
     *
     * Redirects to the authorization endpoint unless the
     * authorization has already been granted.
     */
    SGWTConnectCore.prototype.requestAuthorization = function () {
        // Check if the user is currently signed in to prevent
        // unnecessary sign-in redirects on application load:
        if (this.isAuthorized()) {
            return this.restartTokenExpirationTimer().enablePeriodicalRenewal();
        }
        // At this point the token may not exist, has expired
        // or the authorization server has responded with an
        // error. In any case we clear the current authorization
        // response:
        this.storage.removeItem('AUTH');
        // Cross-Site Request Forgery (CSRF) protection:
        var state = (0, SGWTConnectAuthorizationUri_1.generateCryptoStrongRandomValue)();
        this.storage.setItem("".concat(state, ".STATE"), state);
        // Remember the current URL, we will need to return
        // the user here after authorization is completed:
        this.storage.setItem("".concat(state, ".RETURN_URL"), location.href);
        // Initiate the authorization process by redirecting
        // the current page to the authorization endpoint:
        location.replace((0, SGWTConnectAuthorizationUri_1.generateAuthorizationUri)(this.config, state));
        return this;
    };
    /**
     * Clears the local authorization data and redirects
     * to end_session_endpoint if OpenID is enabled and
     * has loaded the relevant metadata.
     */
    SGWTConnectCore.prototype.discardAuthorization = function () {
        // Redirect is only possible if OpenID Metadata was
        // successfully retrieved from the "well-known" endpoint:
        if (this.openIDMetadata) {
            // Obtain the parameters required for the logout request:
            // https://openid.net/specs/openid-connect-session-1_0.html#RPLogout
            var id_token_hint = this.getIdToken();
            var post_logout_redirect_uri = this.config.post_logout_redirect_uri;
            var end_session_endpoint = this.openIDMetadata.end_session_endpoint;
            // Generate the end session URL:
            var endSessionURL_1 = "".concat(end_session_endpoint, "?id_token_hint=").concat(id_token_hint) +
                (post_logout_redirect_uri ? "&post_logout_redirect_uri=".concat(post_logout_redirect_uri) : '');
            // Schedule termination of the OpenID session:
            this.once(SGWTConnectEventList_1.EVENT_AUTH_DISCARDED, function () {
                window.location.replace(endSessionURL_1);
            });
        }
        // Discard the authorization data which will trigger
        // EVENT_AUTH_DISCARDED that we subscribed to above:
        return this.discardAuthorizationLocally();
    };
    /**
     * Initiates a silent renewal of the token.
     *
     * Because the renewal is transparent to the user, we
     * discourage usage of this method before calling `requestAuthorization`
     * as this will not display the authorization approval
     * interface.
     */
    SGWTConnectCore.prototype.renewAuthorization = function (callback) {
        return this.requestAuthorizationWithoutPrompt({}, callback);
    };
    /**
     * Determines if the authorization has been granted.
     *
     * The state of having authorization is determined by the presence
     * of a non-expired access_token. This method does not tell
     * whether the authorization has been revoked or not,
     * or whether the granted scopes are identical to the
     * requested ones.
     */
    SGWTConnectCore.prototype.isAuthorized = function () {
        var authorization = this.storage.getItem('AUTH');
        return Boolean(authorization && authorization.access_token) && authorization.expires_at > Date.now();
    };
    /**
     * Returns the authorization error or null if there is none.
     */
    SGWTConnectCore.prototype.getAuthorizationError = function () {
        var authorization = this.storage.getItem('AUTH');
        return authorization && authorization.error ? new SGWTConnectError_1.SGWTConnectError(authorization.error) : null;
    };
    /**
     * Generates an "Authorization" HTTP header value.
     *
     * Call this method and use the return value as Authorization header
     * value every time you make an HTTP request to your backend API.
     *
     *    const authHttpHeader = sgwtConnect.getAuthorizationHeader();
     *    if (authHttpHeader) {
     *      headers.set('Authorization', authHttpHeader);
     *    }
     *
     * Currently only the "Bearer" token type is supported.
     */
    SGWTConnectCore.prototype.getAuthorizationHeader = function () {
        var authorization = this.storage.getItem('AUTH');
        if (!this.isAuthorized()) {
            return null;
        }
        if (authorization.token_type && authorization.token_type.toLowerCase() === 'bearer') {
            return "Bearer ".concat(authorization.access_token);
        }
        throw new SGWTConnectError_1.SGWTConnectError(SGWTConnectErrorList_1.ERR_UNSUPPORTED_TOKEN_TYPE);
    };
    /**
     * Returns the scope granted by the authorization server as a space-delimited string.
     */
    SGWTConnectCore.prototype.getGrantedScope = function () {
        // Make sure that the authorization request was successful:
        if (!this.isAuthorized()) {
            return null;
        }
        // Scope is part of authorization server response:
        var authorization = this.storage.getItem('AUTH');
        // Return the scope as is (e.g. "profile openid mail").
        // We leave it up to developer to parse and compare to
        // the requested scope. Also note that the scope
        // may not be returned in the authorization response
        // if it is identical to the scope requested by
        // the client.
        return typeof authorization.scope === 'undefined' ? this.config.scope : authorization.scope;
    };
    /**
     * Determines whether the given scope has been granted.
     *
     *    sgwtConnect.hasGrantedScope('profile openid');
     */
    SGWTConnectCore.prototype.hasGrantedScope = function (checkedScope) {
        var grantedScope = this.getGrantedScope();
        if (typeof grantedScope !== 'string') {
            return false;
        }
        return (0, isScopeListed_1.isScopeListed)(checkedScope, grantedScope);
    };
    /**
     * Obtains the user's ID Token in the form of JWT (JSON Web Token).
     *
     * Use this method to pass user identity to your backend.
     *
     * Calling this method is only useful if the "scope" parameter
     * used to make the authorization request included the "openid"
     * value.
     *
     * See http://openid.net/specs/openid-connect-core-1_0.html#IDToken
     * See https://tools.ietf.org/html/rfc7519
     */
    SGWTConnectCore.prototype.getIdToken = function () {
        // Make sure that the authorization request was successful:
        if (!this.isAuthorized()) {
            return null;
        }
        // Obtain the authorization server response:
        var authorization = this.storage.getItem('AUTH');
        // It is possible that the response_type parameter did not
        // include the "id_token" value while performing an authorization
        // request, so there's no id_token in the current token:
        if (!authorization.id_token) {
            return null;
        }
        // Return the id_token as is so that it can be passed to
        // backend for validation:
        return authorization.id_token;
    };
    /**
     * Obtains basic user information from ID Token claims.
     *
     * Use this method to obtain user id, name, email, etc.
     * without calling the /userinfo endpoint.
     *
     * Calling this method is only useful if the "scope" parameter
     * used to make the authorization request included the "openid"
     * (and optionally "email", "profile", etc.) value.
     *
     * See http://openid.net/specs/openid-connect-core-1_0.html#IDToken
     */
    SGWTConnectCore.prototype.getIdTokenClaims = function () {
        var idToken = this.getIdToken();
        // The ID Token could be missing in the following cases:
        //  1. Authorization not requested
        //  2. Authorization not granted
        //  3. id_token not requested in the authorization request
        //  4. id_token not granted in the authorization response
        if (idToken === null) {
            return null;
        }
        // The id_token conforms to an industry standard (IETF RFC 7519)
        // and contains three parts (header, body, signature) separated
        // by "." (dot) character:
        var idTokenParts = idToken.split('.');
        // The body is the second part (at index 1) containing identity
        // claims about the user:
        var idTokenBody = idTokenParts[1];
        // The id_token body is a base64url encoded JSON structure:
        return JSON.parse(atob(idTokenBody));
    };
    /**
     * Fetches UserInfo Claims from the authorization server.
     *
     * Use this method to obtain user name, email, etc. if
     * you're not requesting id_token or if the id_token
     * does not contain these values.
     *
     * See https://openid.net/specs/openid-connect-core-1_0.html#UserInfo
     */
    SGWTConnectCore.prototype.fetchUserInfo = function (callback) {
        var authorizationHeader = this.getAuthorizationHeader();
        if (authorizationHeader) {
            (0, SGWTConnectOpenIDUserInfo_1.fetchOpenIDUserInfo)(this.config.authorization_endpoint, authorizationHeader, callback);
        }
        else {
            callback(new SGWTConnectError_1.SGWTConnectError(SGWTConnectErrorList_1.ERR_UNAUTHORIZED));
        }
        return this;
    };
    /**
     * Enables silent periodical renewal of the token.
     *
     * Call this method after checking the result of isAuthorized,
     * because it assumes that the authorization request has been
     * performed successfully.
     */
    SGWTConnectCore.prototype.enablePeriodicalRenewal = function () {
        var _this = this;
        // Make sure that we don't have two renewal timers
        // running at the same time:
        if (this.periodicalRenewalTimer !== null) {
            return this;
        }
        // Whenever renewal succeeds (manual or periodical)
        // we need to restart the timer in order to do reduce
        // the number of requests to the authorization endpoint:
        this.addListener(SGWTConnectEventList_1.EVENT_AUTH_RENEW_SUCCESS, this.restartRenewalTimer);
        // Start the initial timer. We're using setTimeout to be able
        // to adjust the duration depending on renew success/error:
        this.periodicalRenewalTimer = window.setTimeout(function () {
            _this.renewResiliently();
        }, this.getRenewalTimeout());
        return this;
    };
    /**
     * Disables silent periodical renewal of the token.
     */
    SGWTConnectCore.prototype.disablePeriodicalRenewal = function () {
        // Make sure that the backoff delay is reset so that next time
        // we start the periodical renewal, it is back to default value:
        this.resetRenewalBackoff();
        // Stop restarting periodical renewal on renew success:
        this.removeListener(SGWTConnectEventList_1.EVENT_AUTH_RENEW_SUCCESS, this.restartRenewalTimer);
        // Clear the current timeout:
        if (typeof this.periodicalRenewalTimer === 'number') {
            window.clearTimeout(this.periodicalRenewalTimer);
            this.periodicalRenewalTimer = null;
        }
        return this;
    };
    //
    // Private
    // --------------------
    /**
     * Discards the local authorization data,
     * cancels all running timers,
     * and emits `EVENT_AUTH_DISCARDED` event.
     */
    SGWTConnectCore.prototype.discardAuthorizationLocally = function () {
        // The authorization is discarded, so it no longer
        // makes sense to do periodical renewal or maintain
        // the expiration timer:
        this.disablePeriodicalRenewal();
        this.clearTokenExpirationTimer();
        if (this.openIDMetadata) {
            // OpenID Session Management is no longer needed:
            this.disableOpenIDSessionManagement();
        }
        // Actually discard the authorization data:
        this.storage.removeItem('AUTH');
        // Notify the interested parties:
        this.emit(SGWTConnectEventList_1.EVENT_AUTH_DISCARDED);
        return this;
    };
    /**
     * Initiates a silent renewal of the token
     * with additional options that are not available
     * in the public API.
     */
    SGWTConnectCore.prototype.requestAuthorizationWithoutPrompt = function (_a, callback) {
        var _this = this;
        var id_token_hint = _a.id_token_hint;
        // Cross-Site Request Forgery (CSRF) protection:
        var state = (0, SGWTConnectAuthorizationUri_1.generateCryptoStrongRandomValue)();
        // Generate the complete authorization endpoint URL
        // where id_token_hint is used to validate session
        // after receiving a "changed" event from OpenID
        // Session Management frame:
        var authorizationEndpoint = id_token_hint
            ? (0, SGWTConnectAuthorizationUri_1.generateAuthorizationUri)(this.config, state, { prompt: 'none', id_token_hint: id_token_hint })
            : (0, SGWTConnectAuthorizationUri_1.generateAuthorizationUri)(this.config, state, { prompt: 'none' });
        // Because OAuth2 protocol insists on providing access_token
        // in the fragment, we're unable to use XMLHttpRequest as it
        // transparently follows all redirects and the fragment is lost.
        // To solve this issue we use an iframe which gives us access
        // to window.location containing the access_token in its fragment.
        var iframe = document.createElement('iframe');
        // When both of these flags are set to true, then it is safe
        // to remove the iframe from the DOM
        //  1. we must not remove the iframe before it emits the "load"
        //     event because in flight resources may get canceled causing
        //     some browsers to remove them even from the top-level window
        //  2. we must wait for the message from iframe because it may
        //     be emitted after(!) "load" event; removing the iframe from
        //     the DOM will cancel the message in flight
        var isIframeLoaded = false;
        var isMessageReceivedFromIframe = false;
        // Iframe timeout in case it does not message or does not load:
        var iframeTimeoutTimer = undefined;
        // The redirect_uri may point to another domain preventing
        // the iframe from accessing the storage to set the token,
        // so it will message the top-level window using cross-origin
        // messaging protocol with window.postMessage. This function
        // securely handles the received message:
        var iframeMessageHandler = function (event) {
            // Make sure that the event comes from the renewal iframe:
            if (event.source !== iframe.contentWindow) {
                return;
            }
            // Make sure that the event comes from redirect_uri page:
            if (!(0, isSameOrigin_1.isSameOrigin)(event.origin, _this.config.redirect_uri)) {
                return;
            }
            // Third party scripts use the same API to send messages,
            // so make sure we're dealing with our own event:
            if (!event.data || typeof event.data !== 'object') {
                return;
            }
            if (event.data.type !== 'SGWTConnectFragment') {
                return;
            }
            // All event validity checks have passed; we consider
            // the event as genuine and can flag it as received
            // so that the "load" event handler knows about it:
            isMessageReceivedFromIframe = true;
            // We expect a single message, so the listener is no longer needed:
            window.removeEventListener('message', iframeMessageHandler);
            // We have received a message from the iframe,
            // the timeout no longer makes sense:
            window.clearTimeout(iframeTimeoutTimer);
            // We can remove the iframe if it has already loaded:
            if (isIframeLoaded) {
                document.body.removeChild(iframe);
            }
            // Extract the authorization server response from the message:
            var authorization = (0, SGWTConnectServerResponse_1.normalizeAuthServerResponse)((0, SGWTConnectServerResponse_1.parseOAuth2Fragment)(event.data.fragment));
            // Cross-Site Request Forgery (CSRF) protection:
            if (authorization.state !== state) {
                var error = new SGWTConnectError_1.SGWTConnectError(SGWTConnectErrorList_1.ERR_STATE_MISMATCH);
                _this.emit(SGWTConnectEventList_1.EVENT_AUTH_RENEW_ERROR, error);
                if (callback) {
                    callback(error);
                }
                return;
            }
            // Notify the subscribers about renewal completion:
            var errorCode = authorization.error;
            if (errorCode) {
                // Notify the API consumer about the authorization failure:
                var error = new SGWTConnectError_1.SGWTConnectError(errorCode);
                _this.emit(SGWTConnectEventList_1.EVENT_AUTH_RENEW_ERROR, error);
                if (callback) {
                    callback(error);
                }
            }
            else {
                // Persist the authorization server response prior to emitting
                // events and calling the completion callback so that the token
                // is available to the completion event handlers:
                _this.storage.setItem('AUTH', authorization);
                // The authorization data has been updated, which means that
                // the expiration time has changed, so we need to restart
                // the expiration timer:
                _this.restartTokenExpirationTimer();
                // Although rare, the authorization state might be initiated
                // by calling renewAuthorization instead of requestAuthorization;
                // in this case we need to enable OpenID Session Management
                // on "renewal" success:
                _this.enableOpenIDSessionManagement();
                // Notify the API consumer about the authorization success:
                _this.emit(SGWTConnectEventList_1.EVENT_AUTH_RENEW_SUCCESS);
                if (callback) {
                    callback(null);
                }
            }
        };
        // Securely handles the "load" event of the renewal iframe
        // by removing event listeners and the iframe itself;
        // also emits an error event in case no message was
        // received from the iframe before "load" event:
        var iframeLoadHandler = function (event) {
            // Make sure the event is genuine:
            if (!event.isTrusted) {
                return;
            }
            // We consider this event as genuine and can flag it as received
            // so that the iframe message event handler knows about it:
            isIframeLoaded = true;
            // The "load" event can only happen once, so the listener
            // is no longer needed:
            iframe.removeEventListener('load', iframeLoadHandler);
            // We can remove the iframe from the DOM if we have already
            // received the message from it:
            if (isMessageReceivedFromIframe) {
                document.body.removeChild(iframe);
            }
        };
        // Expecting the iframe to send us a message
        // from the redirect_uri page using cross-origin
        // messaging protocol with window.postMessage:
        window.addEventListener('message', iframeMessageHandler);
        // We need to listen to the "load" event because
        // we expect the iframe to load completely
        // before removing it from the DOM, otherwise
        // canceled resource requests may destroy already
        // loaded resources from the top-level window:
        iframe.addEventListener('load', iframeLoadHandler);
        // The iframe may never load or fail to send the message due to
        // changes in network, server failure, or some other error,
        // but we still need to emit+callback, so we use a timeout:
        iframeTimeoutTimer = window.setTimeout(function () {
            window.clearTimeout(iframeTimeoutTimer);
            // Cleanup event listeners:
            iframe.removeEventListener('load', iframeLoadHandler);
            window.removeEventListener('message', iframeMessageHandler);
            // Cleanup the DOM:
            document.body.removeChild(iframe);
            // Notify the API consumer about the renewal failure:
            var error = new SGWTConnectError_1.SGWTConnectError(SGWTConnectErrorList_1.ERR_RENEW_TIMEOUT);
            _this.emit(SGWTConnectEventList_1.EVENT_AUTH_RENEW_ERROR, error);
            if (callback) {
                callback(error);
            }
        }, this.periodicalRenewalTimeout);
        // Provide a way for setupSGWTConnect factory to figure out
        // whether the redirect is part of silent renewal or not:
        iframe.setAttribute(exports.SGWTConnectRenewalFrameAttribute, '');
        iframe.style.display = 'none';
        iframe.setAttribute('src', authorizationEndpoint);
        this.appendElementToDocumentWhenLoaded(iframe);
        return this;
    };
    /**
     * Ensures that DOM is loaded and appends the element to document body.
     * @param element Element to append to document body.
     */
    SGWTConnectCore.prototype.appendElementToDocumentWhenLoaded = function (element) {
        if (document.body) {
            document.body.appendChild(element);
        }
        else {
            var appendElementAndRemoveListener_1 = function () {
                window.removeEventListener('DOMContentLoaded', appendElementAndRemoveListener_1);
                document.body.appendChild(element);
            };
            window.addEventListener('DOMContentLoaded', appendElementAndRemoveListener_1);
        }
    };
    /**
     * Determines whether the given scope has been requested.
     *
     *    this.hasRequestedScope('profile openid');
     */
    SGWTConnectCore.prototype.hasRequestedScope = function (checkedScope) {
        return (0, isScopeListed_1.isScopeListed)(checkedScope, this.config.scope);
    };
    /**
     * Resets the periodical renewal backoff delay.
     */
    SGWTConnectCore.prototype.resetRenewalBackoff = function () {
        this.periodicalRenewalBackoff = 1000;
        return this;
    };
    /**
     * Increments the periodical renewal backoff delay.
     *
     * Used to reduce authorization server load when periodical
     * renewals fail for some reason.
     */
    SGWTConnectCore.prototype.incrementRenewalBackoff = function () {
        this.periodicalRenewalBackoff *= 2;
        return this;
    };
    /**
     * Clears the token expiration timer.
     */
    SGWTConnectCore.prototype.clearTokenExpirationTimer = function () {
        if (typeof this.tokenExpirationTimer === 'number') {
            window.clearTimeout(this.tokenExpirationTimer);
            this.tokenExpirationTimer = null;
        }
        return this;
    };
    /**
     * Clears the existing token expiration timer and starts a new one.
     */
    SGWTConnectCore.prototype.restartTokenExpirationTimer = function () {
        var _this = this;
        // Ensure that there is only one timer at all times:
        this.clearTokenExpirationTimer();
        // Assuming that the client is currently authorized:
        var authorization = this.storage.getItem('AUTH');
        var timeUntilTokenExpiration = authorization.expires_at - Date.now();
        this.tokenExpirationTimer = window.setTimeout(function () {
            _this.emit(SGWTConnectEventList_1.EVENT_AUTH_EXPIRED);
        }, timeUntilTokenExpiration);
        return this;
    };
    /**
     * Obtains the timeout (in ms) until token renewal.
     */
    SGWTConnectCore.prototype.getRenewalTimeout = function () {
        var authorization = this.storage.getItem('AUTH');
        // Temporary workaround for the authorization endpoint issue,
        // revert to `- authorization.expires_in / 4` when possible:
        var renewalTime = authorization.expires_at - authorization.expires_in * 0.6;
        var renewalTimePositive = Math.max(renewalTime - Date.now(), 0);
        // The timeout should never be bigger than 2147483647, since setTimeout uses
        // a 32 bits number (cf. https://stackoverflow.com/a/3468650/26457)
        // When the token has an extremely long TTL such as 100 days, this timeout should
        // not exceed this 2147483647 value.
        return Math.min(renewalTimePositive, 2147483647);
    };
    /**
     * Renews the authorization token resiliently.
     *
     * When doing periodical renewal we must be resilient
     * on authorization failures and never abort the timer.
     * Even in case of an error we restart the timer,
     * but with an incrementally increasing interval
     * to avoid network saturation.
     */
    SGWTConnectCore.prototype.renewResiliently = function () {
        var _this = this;
        return this.renewAuthorization(function (error) {
            if (error) {
                _this.periodicalRenewalTimer = window.setTimeout(function () {
                    _this.renewResiliently();
                }, _this.periodicalRenewalBackoff);
                _this.incrementRenewalBackoff();
            }
            else {
                _this.resetRenewalBackoff();
            }
        });
    };
    /**
     * Restarts an existing renewal timer.
     */
    SGWTConnectCore.prototype.restartRenewalTimer = function () {
        var _this = this;
        // Do not attempt to restart a non-existing timer:
        if (this.periodicalRenewalTimer === null) {
            return this;
        }
        // Every time the renewal is restarted we should reset
        // the renewal backoff delay:
        this.resetRenewalBackoff();
        // Cancel the current renewal timeout:
        window.clearTimeout(this.periodicalRenewalTimer);
        // Restart the renewal timer:
        this.periodicalRenewalTimer = window.setTimeout(function () {
            return _this.renewResiliently();
        }, this.getRenewalTimeout());
        return this;
    };
    /**
     * Enables OpenID session management if not previously enabled.
     */
    SGWTConnectCore.prototype.enableOpenIDSessionManagement = function () {
        var _this = this;
        // Make sure that the session management has not already been enabled:
        if (this.openIDCheckSessionIframe && this.openIDCheckSessionIframe.parentNode) {
            return this;
        }
        // Make sure that OpenID has actually been configured:
        if (!this.openIDMetadata || !this.hasRequestedScope('openid')) {
            return this;
        }
        // Enabling session management only makes sense
        // if the authorization has already been granted:
        if (!this.isAuthorized()) {
            return this;
        }
        // To check OpenID session we will be polling an iframe
        // loaded from check_session_iframe as recommended by
        // https://openid.net/specs/openid-connect-session-1_0.html#ChangeNotification
        this.openIDCheckSessionIframe = document.createElement('iframe');
        this.openIDCheckSessionIframe.style.display = 'none';
        this.openIDCheckSessionIframe.setAttribute(exports.SGWTConnectCheckSesssionFrameAttribute, '');
        // Expecting the iframe to send us a message using
        // messaging protocol with window.postMessage:
        window.addEventListener('message', this.handleOpenIDCheckSessionMessage.bind(this));
        // Securely handles the "load" event of the OpenID check_session_iframe:
        var checkSessionIframeLoadHandler = function (event) {
            // Make sure the event is genuine:
            if (!event.isTrusted) {
                return;
            }
            // Make sure that the iframe still exists:
            if (!_this.openIDCheckSessionIframe) {
                return;
            }
            // The "load" event can only happen once,
            // so the listener is no longer needed:
            _this.openIDCheckSessionIframe.removeEventListener('load', checkSessionIframeLoadHandler);
            // Start polling:
            _this.restartOpenIDSessionValidationTimer();
        };
        this.openIDCheckSessionIframe.addEventListener('load', checkSessionIframeLoadHandler);
        this.openIDCheckSessionIframe.setAttribute('src', this.openIDMetadata.check_session_iframe);
        this.appendElementToDocumentWhenLoaded(this.openIDCheckSessionIframe);
        return this;
    };
    /**
     * Disables OpenID session management.
     */
    SGWTConnectCore.prototype.disableOpenIDSessionManagement = function () {
        // Stop listening to messages:
        window.removeEventListener('message', this.handleOpenIDCheckSessionMessage);
        // Stop polling the check_session_iframe:
        if (typeof this.openIDCheckSessionTimerId === 'number') {
            window.clearTimeout(this.openIDCheckSessionTimerId);
            this.openIDCheckSessionTimerId = undefined;
        }
        // Remove the iframe:
        if (this.openIDCheckSessionIframe) {
            document.body.removeChild(this.openIDCheckSessionIframe);
            this.openIDCheckSessionIframe = undefined;
        }
        return this;
    };
    /**
     * Restarts OpenID session validation timer.
     */
    SGWTConnectCore.prototype.restartOpenIDSessionValidationTimer = function () {
        // There should be only one OpenID session validation timer:
        window.clearTimeout(this.openIDCheckSessionTimerId);
        // Schedule a new timer:
        this.openIDCheckSessionTimerId = window.setTimeout(this.validateOpenIDSession.bind(this), this.openIDCheckSessionTimerInterval);
    };
    /**
     * Validates the existing OpenID session.
     */
    SGWTConnectCore.prototype.validateOpenIDSession = function () {
        var _this = this;
        if (this.isAuthorized()) {
            // The current authorization state retrieved from the storage
            // indicates that the session is valid, however it might be
            // terminated already at the authorization server.
            // To validate the session, we need to send a message
            // to the check_session iframe, but before that we make sure
            // that we have metadata and that the check_session iframe
            // still exists in the DOM.
            if (!this.openIDMetadata) {
                return;
            }
            if (this.openIDCheckSessionIframe && this.openIDCheckSessionIframe.contentWindow) {
                var authorization = this.storage.getItem('AUTH');
                this.openIDCheckSessionIframe.contentWindow.postMessage("".concat(this.config.client_id, " ").concat(authorization.session_state), this.openIDMetadata.check_session_iframe);
                this.restartOpenIDSessionValidationTimer();
            }
            else {
                // In case the check_session iframe no longer exists,
                // we try to recreate it be re-enabling session management:
                this.enableOpenIDSessionManagement();
            }
        }
        else {
            // In case the authorization state is lost (storage cleared)
            // we attempt a silent authorization renewal. If the session
            // is still valid on the authorization server, the renewal
            // should succeed and we just restart the timer; otherwise
            // we treat it as loss of authorization and act accordingly.
            this.renewAuthorization(function (error) {
                if (error) {
                    _this.discardAuthorizationLocally();
                }
                else {
                    _this.restartOpenIDSessionValidationTimer();
                }
            });
        }
    };
    /**
     * Securely handles events from check_session_iframe.
     * @param event MessageEvent
     */
    SGWTConnectCore.prototype.handleOpenIDCheckSessionMessage = function (event) {
        var _this = this;
        // Sanity checks:
        if (!this.openIDCheckSessionIframe || !this.openIDMetadata) {
            return;
        }
        // Make sure that the event comes from the check_session_iframe:
        if (event.source !== this.openIDCheckSessionIframe.contentWindow) {
            return;
        }
        if (!(0, isSameOrigin_1.isSameOrigin)(event.origin, this.openIDMetadata.check_session_iframe)) {
            return;
        }
        // Expecting event.data to be either "unchanged", "changed", or "error".
        // See https://openid.net/specs/openid-connect-session-1_0.html#RPiframe
        if (event.data === 'unchanged') {
            // Session has not changed, so we only notify
            // (mostly for logging and testing purposes):
            this.emit(SGWTConnectEventList_1.EVENT_OPENID_SESSION_UNCHANGED);
        }
        else if (event.data === 'changed') {
            this.emit(SGWTConnectEventList_1.EVENT_OPENID_SESSION_CHANGED);
            // Perform re-authentication with prompt=none
            // sending the old id_token as id_token_hint:
            var prevIdToken = this.getIdToken();
            var prevIdTokenClaims_1 = this.getIdTokenClaims();
            this.requestAuthorizationWithoutPrompt({ id_token_hint: prevIdToken }, function (error) {
                if (!error) {
                    // If we receive a new ID Token for the same user
                    // then we simply update the values
                    var nextIdTokenClaims = _this.getIdTokenClaims();
                    if (nextIdTokenClaims && prevIdTokenClaims_1 && nextIdTokenClaims.sub === prevIdTokenClaims_1.sub) {
                        return;
                    }
                }
                // In other cases, such as error response,
                // or absence of ID Token, or it was delivered
                // for another user, we discard authorization:
                _this.discardAuthorizationLocally();
            });
        }
        else if (event.data === 'error') {
            // In case of communication error with check_session_iframe
            // we notify the user and wait for fallback to periodical
            // renewal to check the session validity:
            this.emit(SGWTConnectEventList_1.EVENT_OPENID_CHECK_SESSION_ERROR, new SGWTConnectError_1.SGWTConnectError(SGWTConnectErrorList_1.ERR_OPENID_CHECK_SESSION_ERROR));
        }
    };
    return SGWTConnectCore;
}(events_1.EventEmitter));
exports.SGWTConnectCore = SGWTConnectCore;
//# sourceMappingURL=SGWTConnectCore.js.map